import { useQuery } from "@tanstack/react-query";
import {
  getAdminDiagnostics,
  getAdminJobs,
  getAdminPerformance,
  getAdminStreams,
  getTranscodeProgress,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import type {
  AdminDiagnostics,
  AdminJobsPayload,
  AdminPerformance,
  AdminStreamsPayload,
  TranscodeProgressMap,
} from "@/lib/types";

/**
 * M8a — shared admin TanStack Query hooks.
 *
 * The shell mounts one `useAdminDiagnostics` (10 s) + one
 * `useAdminStreams` (interval per-route) so the status strip + every
 * sub-route share a single data flow. Two route-scoped polls layer
 * on top:
 *   - `useAdminPerformance` — 5 s on /admin/live + /admin/performance
 *   - `useTranscodeProgress` — 5 s on /admin/matches only when at least
 *     one slot is in `transcoding` (the caller gates `enabled`)
 *
 * All polls:
 *   - pause when the document is hidden (`refetchIntervalInBackground:
 *     false`) — the operator isn't watching while the tab is in the
 *     background, and the 10 s + 5 s loops would otherwise hammer the
 *     backend from every parked tab
 *   - share a single cache key per endpoint so navigation between
 *     /admin/* sub-routes doesn't refetch the same data
 *   - read the (already-injected) Bearer auth token via `apiFetch`'s
 *     normal flow — no extra header plumbing here
 *
 * The shell status strip mounts the 10 s diagnostics + streams loops
 * at the shell level (above the sub-route outlet), so sub-route
 * navigation doesn't re-mount them. Sub-routes opt into faster
 * cadence by mounting `useAdminPerformance` etc. additionally.
 *
 * No `staleTime` is set here: every admin signal is intentionally
 * non-stale (the strip should reflect the latest tick), so a manual
 * refetch on tab-focus re-runs the queryFn immediately.
 */

/** Status-strip + Overview KPIs (10 s on the shell). */
export function useAdminDiagnostics(options?: {
  /** Override the default 10 s. `false` disables polling (used by
   * route components that only want a one-shot read). */
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 10_000;
  return useQuery<AdminDiagnostics>({
    queryKey: unscopedKey("admin-diagnostics"),
    queryFn: getAdminDiagnostics,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Performance + encoder telemetry (5 s on Live + Performance). */
export function useAdminPerformance(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 5_000;
  return useQuery<AdminPerformance>({
    queryKey: unscopedKey("admin-performance"),
    queryFn: getAdminPerformance,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Global-admin background-job health strip (10 s on Performance). */
export function useAdminJobs(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 10_000;
  return useQuery<AdminJobsPayload>({
    queryKey: unscopedKey("admin-jobs"),
    queryFn: getAdminJobs,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Active streams + kill blocks. Default 10 s on the shell; routes
 * that need 5 s (e.g. /admin/live) pass `refetchIntervalMs: 5_000`. */
export function useAdminStreams(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 10_000;
  return useQuery<AdminStreamsPayload>({
    queryKey: unscopedKey("admin-streams"),
    queryFn: getAdminStreams,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Live transcode progress — public-no-auth endpoint. Polled only
 * when `enabled` is true (the caller gates on "any slot currently
 * transcoding" so an idle library page doesn't poll). */
export function useTranscodeProgress(options: {
  enabled: boolean;
  refetchIntervalMs?: number;
}) {
  const interval = options.refetchIntervalMs ?? 5_000;
  return useQuery<TranscodeProgressMap>({
    queryKey: unscopedKey("transcode-progress"),
    queryFn: getTranscodeProgress,
    refetchInterval: options.enabled ? interval : false,
    refetchIntervalInBackground: false,
    enabled: options.enabled,
  });
}
